import { api } from "@/lib/api-client"
import { useQuery } from "@tanstack/react-query"
import { useSession } from "@zentjes/auth/client"
import { type ApiDataEnvelope, unwrapApiData } from "@/shared/lib/api-envelope"
import type { TNotification } from "@/types"

type NotificationsResponse =
    | TNotification[]
    | { items: TNotification[]; nextCursor: string | null }
    | ApiDataEnvelope<TNotification[] | { items: TNotification[]; nextCursor: string | null }>
    | { error: string }
type UnreadCountResponse = { count: number } | ApiDataEnvelope<{ count: number }> | { error: string }

/** Shared query keys for notifications data. */
export const notificationsQueryKeys = {
    all: ["notifications"] as const,
    byUser: (userId?: string) => ["notifications", userId] as const,
    unreadCount: (userId?: string) => ["notifications", "count", userId] as const,
}

function toNotificationList(payload: NotificationsResponse): TNotification[] {
    const unwrapped = unwrapApiData(payload as any)
    if (Array.isArray(unwrapped)) {
        return unwrapped
    }

    if (unwrapped && typeof unwrapped === "object" && "items" in unwrapped) {
        return unwrapped.items
    }

    if (Array.isArray(payload)) {
        return payload
    }

    if ("items" in payload) {
        return payload.items
    }

    return []
}

export function useNotificationsQuery() {
    const { data: session } = useSession()
    const userId = session?.user?.id

    return useQuery<TNotification[]>({
        queryKey: notificationsQueryKeys.byUser(userId),
        queryFn: async (): Promise<TNotification[]> => {
            if (!userId) return []
            const res = await api.notifications.$get({ query: {} })
            const data = (await res.json()) as NotificationsResponse
            return toNotificationList(data)
        },
        enabled: !!userId,
        refetchInterval: 30000,
    })
}

export function useUnreadCountQuery() {
    const { data: session } = useSession()
    const userId = session?.user?.id

    return useQuery<number>({
        queryKey: notificationsQueryKeys.unreadCount(userId),
        queryFn: async (): Promise<number> => {
            if (!userId) return 0
            const res = await api.notifications.count.$get()
            const data = (await res.json()) as UnreadCountResponse
            if ("error" in data) return 0
            return unwrapApiData(data as any).count ?? 0
        },
        enabled: !!userId,
        refetchInterval: 30000,
    })
}
